/**
 * Módulo Miembros (Nuevo / Editar) — único para administrador, supervisor y líder.
 */
import { useState, useMemo, useEffect, useCallback } from "react";
import { Plus, Edit, Trash2, Phone, Building2, User, Archive, FileSpreadsheet, FileText, Search } from "lucide-react";
import { ExcelExportLabel, excelExportButtonA11y } from "@/components/excelExportLabel";
import {
  buildMembersExportRows,
  buildMembersFilterReportTitle,
  exportMembersFilteredCsv,
  exportMembersFilteredExcel,
  exportMembersFilteredPdf,
} from "@/lib/membersFilteredExport";
import { api } from "@/lib/apiClient";
import {
  memberSexLabel,
  MEMBER_SEX_F,
  MEMBER_SEX_FILTER_ALL,
  MEMBER_SEX_FILTER_UNSPECIFIED,
  MEMBER_SEX_M,
  isValidMemberSexValue,
} from "@/lib/memberSex";
import {
  formatMentor,
  mentorFormStateFromMember,
  mentorTextMatchesBlockedValue,
  isMemberFlagTrue,
} from "@/lib/memberMentorForm";
import { MemberSpiritualTabFields } from "@/components/MemberSpiritualTabFields";
import {
  MEMBER_YOUTH_CAMP_FILTER_ALL_GROUPS,
  MEMBER_SPIRITUAL_STATUS_FILTER_NO_BAPTIZED,
  MEMBER_SPIRITUAL_STATUS_FILTER_NO_ENCOUNTER,
  MEMBER_SPIRITUAL_STATUS_FILTER_NO_MENTOR,
  MEMBER_SPIRITUAL_STATUS_FILTER_OPTIONS,
  YOUTH_CAMP_GROUP_OPTIONS,
  spiritualStatusFilterLabel,
  youthCampGroupLabel,
} from "@/lib/memberFormLabels";
import { NativeDialog, NativeSelect, NativeSwitch, NativeTabs } from "@/components/nativeUi";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  getCellMongoId,
  getDirectChildLeaderMembers,
  memberMatchesCellFilter,
  normalizeRefId,
} from "@/lib/cellIds";

function memberMatchesSearch(member, rawQuery) {
  const query = String(rawQuery || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");
  if (!query) return true;

  const name = String(member.name || "")
    .trim()
    .toLowerCase();
  const phoneDigits = String(member.phone || "").replace(/\D/g, "");
  const queryDigits = query.replace(/\D/g, "");
  const queryLetters = query.replace(/\d/g, "").trim();

  if (queryDigits && !queryLetters) {
    return phoneDigits.includes(queryDigits);
  }

  if (name.includes(query)) return true;

  const tokens = query.split(" ").filter(Boolean);
  if (tokens.length > 0 && tokens.every((token) => name.includes(token))) return true;

  if (queryDigits.length >= 2 && phoneDigits.includes(queryDigits)) return true;

  return false;
}

const MEMBER_PANEL_CARGO_FIELDS = [
  ["leader_id", "Líder"],
  ["co_leader_id", "Co-Líder"],
  ["secretary_id", "Secretario(a)"],
  ["treasurer_id", "Tesorero(a)"],
  ["host_id", "Anfitrión"],
];

function formatMemberSaveError(err) {
  const d = err.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) {
    return d
      .map((x) => {
        if (typeof x === "string") return x;
        const loc = Array.isArray(x.loc) ? x.loc.filter(Boolean).join(".") : "";
        const msg = x.msg || x.message || "";
        return loc ? `${loc}: ${msg}` : msg;
      })
      .filter(Boolean)
      .join(" · ");
  }
  return err.message || "Error al guardar miembro";
}

function memberCargoLines(member, cells) {
  if (!member || !cells?.length) return [];
  const mid = String(member.id || member._id || "").trim();
  if (!mid) return [];
  const out = [];
  for (const cell of cells) {
    const cid = getCellMongoId(cell);
    for (const [field, label] of MEMBER_PANEL_CARGO_FIELDS) {
      if (String(cell[field] || "").trim() === mid) {
        out.push({
          label,
          cellId: cid,
          cellName: cell.name || cid,
        });
      }
    }
  }
  return out;
}

function memberHasCellCargo(memberId, cells) {
  return memberCargoLines({ id: memberId }, cells).length > 0;
}

function memberInServiceDepartment(member) {
  if (!member) return false;
  if (member.serves_in_department === true) return true;
  if (member.active_in_service === true) return true;
  if (String(member.service_department || "").trim()) return true;
  return false;
}

const MEMBER_TRASH_CARGO_MSG =
  "No se puede mover a papelera porque este miembro tiene un cargo asignado en una célula. Primero debe quitar el cargo.";

const MEMBER_TRASH_DEPARTMENT_MSG =
  "No se puede mover a papelera porque este miembro pertenece a un departamento de servicio. Primero debe quitarlo del departamento.";

const MEMBER_PERMANENT_WRONG_PASSWORD_MSG = "Contraseña incorrecta. No se eliminó el miembro.";

const MEMBER_TYPE_LABELS = {
  invitado_potencial: "Invitado Potencial",
  invitado_primer_mes: "Invitado 1er Mes",
  visita_regular: "Visita Regular",
  visita_esporadica: "Visita Esporádica",
  visita: "Visita",
  miembro: "Miembro",
  invitado: "Invitado",
  pastor: "Pastor",
  pastora: "Pastora",
};

function getMemberTypeLabel(type) {
  return MEMBER_TYPE_LABELS[type] || type || "—";
}

function PermanentDeleteMemberDialog({ open, member, onClose, onConfirm, submitting }) {
  const [reasonCode, setReasonCode] = useState("duplicidad");
  const [otherReason, setOtherReason] = useState("");
  const [password, setPassword] = useState("");

  useEffect(() => {
    if (open) {
      setReasonCode("duplicidad");
      setOtherReason("");
      setPassword("");
    }
  }, [open, member?.id]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!reasonCode) {
      alert("Seleccione un motivo");
      return;
    }
    if (reasonCode === "otros" && !String(otherReason).trim()) {
      alert("Indique la explicación cuando el motivo es Otros");
      return;
    }
    if (!String(password).trim()) {
      alert("La contraseña es obligatoria");
      return;
    }
    onConfirm({
      reason: reasonCode,
      other_reason: reasonCode === "otros" ? String(otherReason).trim() : undefined,
      password: String(password),
    });
  };

  if (!member) return null;

  return (
    <NativeDialog open={open} onClose={onClose} className="w-full max-w-md p-6">
      <h2 className="text-xl font-bold mb-2 text-red-800">Eliminar definitivamente</h2>
      <p className="text-sm text-gray-600 mb-4">
        Esta acción eliminará permanentemente a <span className="font-medium">{member.name}</span> de la base de datos.
        No se puede deshacer.
      </p>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-2">
          <Label>Motivo *</Label>
          <NativeSelect value={reasonCode} onChange={setReasonCode}>
            <option value="duplicidad">Duplicidad</option>
            <option value="por_pruebas">Por pruebas</option>
            <option value="cambio_domicilio">Cambio de domicilio</option>
            <option value="otros">Otros</option>
          </NativeSelect>
        </div>
        {reasonCode === "otros" ? (
          <div className="space-y-2">
            <Label>Explicación *</Label>
            <Input
              value={otherReason}
              onChange={(e) => setOtherReason(e.target.value)}
              placeholder="Describa el motivo"
              required
            />
          </div>
        ) : null}
        <div className="space-y-2">
          <Label>Contraseña de confirmación *</Label>
          <Input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Ingrese la contraseña"
            required
            autoComplete="off"
          />
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="outline" onClick={onClose} disabled={submitting}>
            Cancelar
          </Button>
          <Button type="submit" variant="destructive" disabled={submitting}>
            {submitting ? "Eliminando…" : "Eliminar definitivamente"}
          </Button>
        </div>
      </form>
    </NativeDialog>
  );
}

function MembersTrashDialog({ open, onClose, canPermanentDelete, onPermanentDeleted }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [permanentTarget, setPermanentTarget] = useState(null);
  const [permanentSubmitting, setPermanentSubmitting] = useState(false);

  const loadTrash = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/members/trash");
      setItems(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      setItems([]);
      alert(formatMemberSaveError(err) || "No se pudo cargar la papelera de miembros");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) loadTrash();
  }, [open, loadTrash]);

  const handlePermanentConfirm = async (body) => {
    const mid = String(permanentTarget?.id || "").trim();
    if (!mid) return;
    setPermanentSubmitting(true);
    try {
      const res = await api.delete(`/members/${mid}/permanent`, { data: body });
      alert(res.data?.message || "Miembro eliminado definitivamente");
      setPermanentTarget(null);
      await loadTrash();
      onPermanentDeleted?.();
    } catch (err) {
      const msg = formatMemberSaveError(err);
      if (msg.includes("Contraseña incorrecta") || err.response?.status === 400) {
        alert(msg || MEMBER_PERMANENT_WRONG_PASSWORD_MSG);
      } else {
        alert(msg || "Error al eliminar definitivamente el miembro");
      }
    } finally {
      setPermanentSubmitting(false);
    }
  };

  return (
    <>
    <NativeDialog open={open} onClose={onClose} className="w-full max-w-5xl p-6 max-h-[90vh] overflow-y-auto">
      <div className="flex justify-between items-start gap-4 mb-4">
        <div>
          <h2 className="text-xl font-bold text-gray-800">Papelera de miembros</h2>
          <p className="text-sm text-gray-600 mt-1">
            Miembros movidos a papelera (borrado lógico con motivo).
            {canPermanentDelete ? " Puede eliminarlos definitivamente si no tienen relaciones activas." : ""}
          </p>
        </div>
        <Button type="button" variant="outline" size="sm" onClick={onClose}>
          Cerrar
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="w-10 h-10 border-4 border-amber-600 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : items.length === 0 ? (
        <p className="text-gray-500 text-center py-8">No hay miembros en la papelera.</p>
      ) : (
        <div className="overflow-x-auto border rounded-lg">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="text-left p-3 font-semibold text-gray-700">Nombre</th>
                <th className="text-left p-3 font-semibold text-gray-700">Teléfono</th>
                <th className="text-left p-3 font-semibold text-gray-700">Célula</th>
                <th className="text-left p-3 font-semibold text-gray-700">Tipo de miembro</th>
                <th className="text-left p-3 font-semibold text-gray-700">Motivo</th>
                {canPermanentDelete ? (
                  <th className="text-right p-3 font-semibold text-gray-700">Acciones</th>
                ) : null}
              </tr>
            </thead>
            <tbody>
              {items.map((row) => (
                <tr key={row.id} className="border-b hover:bg-gray-50">
                  <td className="p-3 font-medium text-gray-900">{row.name}</td>
                  <td className="p-3 text-gray-600">{row.phone || "—"}</td>
                  <td className="p-3 text-gray-600">{row.cell_name || "Sin célula"}</td>
                  <td className="p-3 text-gray-600">{getMemberTypeLabel(row.member_type)}</td>
                  <td className="p-3 text-gray-600">{row.deleted_reason || "—"}</td>
                  {canPermanentDelete ? (
                    <td className="p-3 text-right">
                      <Button
                        type="button"
                        size="sm"
                        variant="destructive"
                        disabled={permanentSubmitting}
                        onClick={() => setPermanentTarget(row)}
                      >
                        <Trash2 className="w-4 h-4 mr-1" />
                        Eliminar definitivamente
                      </Button>
                    </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </NativeDialog>

    <PermanentDeleteMemberDialog
      open={Boolean(permanentTarget)}
      member={permanentTarget}
      onClose={() => !permanentSubmitting && setPermanentTarget(null)}
      onConfirm={handlePermanentConfirm}
      submitting={permanentSubmitting}
    />
    </>
  );
}

function MoveMemberToTrashDialog({ open, member, onClose, onConfirm, submitting }) {
  const [reasonCode, setReasonCode] = useState("duplicidad");
  const [reasonDetail, setReasonDetail] = useState("");

  useEffect(() => {
    if (open) {
      setReasonCode("duplicidad");
      setReasonDetail("");
    }
  }, [open, member?.id]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (reasonCode === "otros" && !String(reasonDetail).trim()) {
      alert("Indique la explicación cuando el motivo es Otros");
      return;
    }
    onConfirm({
      reason_code: reasonCode,
      reason_detail: reasonCode === "otros" ? String(reasonDetail).trim() : undefined,
    });
  };

  if (!member) return null;

  return (
    <NativeDialog open={open} onClose={onClose} className="w-full max-w-md p-6">
      <h2 className="text-xl font-bold mb-2 text-gray-800">Mover a papelera</h2>
      <p className="text-sm text-gray-600 mb-4">
        El miembro <span className="font-medium">{member.name}</span> no se eliminará de la base de datos; quedará en la
        papelera.
      </p>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-2">
          <Label>Motivo *</Label>
          <NativeSelect value={reasonCode} onChange={setReasonCode}>
            <option value="duplicidad">Duplicidad</option>
            <option value="por_pruebas">Por pruebas</option>
            <option value="cambio_domicilio">Cambio de domicilio</option>
            <option value="otros">Otros</option>
          </NativeSelect>
        </div>
        {reasonCode === "otros" ? (
          <div className="space-y-2">
            <Label>Explicación *</Label>
            <Input
              value={reasonDetail}
              onChange={(e) => setReasonDetail(e.target.value)}
              placeholder="Describa el motivo"
              required
            />
          </div>
        ) : null}
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="outline" onClick={onClose} disabled={submitting}>
            Cancelar
          </Button>
          <Button type="submit" variant="destructive" disabled={submitting}>
            {submitting ? "Enviando…" : "Mover a papelera"}
          </Button>
        </div>
      </form>
    </NativeDialog>
  );
}

export function MembersPanel({ members, cells, onRefresh, canManage, canTrashMembers, canPermanentDeleteMembers }) {
  const [showForm, setShowForm] = useState(false);
  const [showTrash, setShowTrash] = useState(false);
  const [trashTarget, setTrashTarget] = useState(null);
  const [trashSubmitting, setTrashSubmitting] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [filterCell, setFilterCell] = useState("");
  /** direct = miembros de la célula; parent_enlace = líderes/co-líderes de hijas directas */
  const [filterMode, setFilterMode] = useState("direct");
  const [filterSex, setFilterSex] = useState(MEMBER_SEX_FILTER_ALL);
  const [filterYouthCamp, setFilterYouthCamp] = useState("");
  const [filterSpiritualStatus, setFilterSpiritualStatus] = useState("");
  const [filterSearch, setFilterSearch] = useState("");
  const [exportBusy, setExportBusy] = useState(false);
  const [activeTab, setActiveTab] = useState("basic");
  const [formData, setFormData] = useState({
    cell_id: "",
    name: "",
    phone: "",
    sex: "",
    age: "",
    marital_status: "",
    birth_date: "",
    encuentro_attended: false,
    encuentro_date: "",
    baptized: false,
    baptism_date: "",
    academic_degree: "",
    address_zone: "",
    attends_services: false,
    service_type: "",
    non_attendance_reason: "",
    attends_cell_regularly: false,
    member_type: "invitado_potencial",
    active_in_service: false,
    serves_in_department: false,
    service_department: "",
    desired_department: "",
    spiritual_gifts: [],
    stopped_attending: false,
    stopped_reason: "",
    extra_care: false,
    inactive_2_months: false,
    has_mentor: false,
    mentor_name: "",
    leader_school_enrolled: false,
    youth_camp_attended: false,
    youth_camp_group: "",
  });

  const editingMemberDoc = useMemo(() => {
    if (!editingMember) return null;
    return members.find((m) => String(m.id || m._id) === String(editingMember)) || null;
  }, [members, editingMember]);

  const cargoInfoForEditing = useMemo(() => memberCargoLines(editingMemberDoc, cells), [editingMemberDoc, cells]);

  const resetForm = () => {
    setFormData({
      cell_id: "",
      name: "",
      phone: "",
      sex: "",
      age: "",
      marital_status: "",
      birth_date: "",
      encuentro_attended: false,
      encuentro_date: "",
      baptized: false,
      baptism_date: "",
      academic_degree: "",
      address_zone: "",
      attends_services: false,
      service_type: "",
      non_attendance_reason: "",
      attends_cell_regularly: false,
      member_type: "invitado_potencial",
      active_in_service: false,
      serves_in_department: false,
      service_department: "",
      desired_department: "",
      spiritual_gifts: [],
      stopped_attending: false,
      stopped_reason: "",
      extra_care: false,
      inactive_2_months: false,
      has_mentor: false,
      mentor_name: "",
      leader_school_enrolled: false,
      youth_camp_attended: false,
      youth_camp_group: "",
    });
    setEditingMember(null);
    setShowForm(false);
    setActiveTab("basic");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const rawMentor = String(formData.mentor_name ?? "")
      .replace(/\u00a0/g, " ")
      .replace(/[\u200B-\u200D\uFEFF]/g, "")
      .trim();
    let mentor_name = formData.has_mentor ? rawMentor : "";
    let has_mentor = Boolean(formData.has_mentor) && mentor_name.length > 0;
    if (has_mentor && mentorTextMatchesBlockedValue(mentor_name)) {
      mentor_name = "";
      has_mentor = false;
    }
    if (!editingMember && !isValidMemberSexValue(formData.sex)) {
      alert("Seleccione el sexo (Masculino o Femenino).");
      return;
    }
    if (formData.sex && !isValidMemberSexValue(formData.sex)) {
      alert("Sexo inválido. Use Masculino o Femenino.");
      return;
    }
    const mentor_name_payload = has_mentor ? mentor_name || null : null;
    const submitData = {
      ...formData,
      sex: isValidMemberSexValue(formData.sex) ? formData.sex : null,
      age: formData.age ? parseInt(formData.age, 10) : null,
      has_mentor,
      mentor_name: mentor_name_payload,
      youth_camp_group:
        formData.youth_camp_attended && formData.youth_camp_group ? formData.youth_camp_group : null,
    };

    try {
      if (editingMember) {
        await api.put(`/members/${editingMember}`, submitData);
      } else {
        await api.post("/members", submitData);
      }
      resetForm();
      onRefresh();
    } catch (err) {
      alert(formatMemberSaveError(err) || "Error al guardar miembro");
    }
  };

  const handleEdit = (member) => {
    const mf = mentorFormStateFromMember(member);
    setFormData({
      cell_id: member.cell_id || "",
      name: member.name || "",
      phone: member.phone || "",
      sex: member.sex || "",
      age: member.age?.toString() || "",
      marital_status: member.marital_status || "",
      birth_date: member.birth_date || "",
      encuentro_attended: member.encuentro_attended || false,
      encuentro_date: member.encuentro_date || "",
      baptized: member.baptized || false,
      baptism_date: member.baptism_date || "",
      academic_degree: member.academic_degree || "",
      address_zone: member.address_zone || "",
      attends_services: member.attends_services || false,
      service_type: member.service_type || "",
      non_attendance_reason: member.non_attendance_reason || "",
      attends_cell_regularly: member.attends_cell_regularly || false,
      member_type: member.member_type || "invitado_potencial",
      active_in_service: member.active_in_service || false,
      serves_in_department: member.serves_in_department || false,
      service_department: member.service_department || "",
      desired_department: member.desired_department || "",
      spiritual_gifts: member.spiritual_gifts || [],
      stopped_attending: member.stopped_attending || false,
      stopped_reason: member.stopped_reason || "",
      extra_care: member.extra_care || false,
      inactive_2_months: member.inactive_2_months || false,
      has_mentor: mf.has_mentor,
      mentor_name: mf.mentor_name,
      leader_school_enrolled: member.leader_school_enrolled || false,
      youth_camp_attended: member.youth_camp_attended || false,
      youth_camp_group: member.youth_camp_group || "",
    });
    setEditingMember(member.id || member._id);
    setShowForm(true);
    setActiveTab("basic");
  };

  const openMoveToTrash = (member) => {
    const mid = String(member.id || member._id || "").trim();
    if (!mid) return;
    if (memberHasCellCargo(mid, cells)) {
      alert(MEMBER_TRASH_CARGO_MSG);
      return;
    }
    if (memberInServiceDepartment(member)) {
      alert(MEMBER_TRASH_DEPARTMENT_MSG);
      return;
    }
    setTrashTarget(member);
  };

  const handleConfirmTrash = async (body) => {
    const mid = String(trashTarget?.id || trashTarget?._id || "").trim();
    if (!mid) return;
    setTrashSubmitting(true);
    try {
      await api.post(`/members/${mid}/trash`, body);
      setTrashTarget(null);
      onRefresh();
    } catch (err) {
      alert(formatMemberSaveError(err) || "Error al mover a la papelera");
    } finally {
      setTrashSubmitting(false);
    }
  };

  const getCellName = (cellId) => {
    const cid = getCellMongoId(cellId) || normalizeRefId(cellId);
    const cell = cells.find((c) => getCellMongoId(c) === cid);
    return cell ? cell.name : "Sin célula";
  };

  const selectedFilterCell = useMemo(() => {
    if (!filterCell) return null;
    return cells.find((c) => getCellMongoId(c) === filterCell) || null;
  }, [cells, filterCell]);

  const applySexFilter = useCallback((list) => {
    if (!filterSex || filterSex === MEMBER_SEX_FILTER_ALL) return list;
    if (filterSex === MEMBER_SEX_FILTER_UNSPECIFIED) {
      return list.filter((m) => !isValidMemberSexValue(m.sex));
    }
    return list.filter((m) => String(m.sex || "").toUpperCase() === filterSex);
  }, [filterSex]);

  const applyYouthCampFilter = useCallback(
    (list) => {
      const f = String(filterYouthCamp || "").trim();
      if (!f) return list;
      if (f === MEMBER_YOUTH_CAMP_FILTER_ALL_GROUPS) {
        return list.filter((m) => isMemberFlagTrue(m.youth_camp_attended));
      }
      const group = f.toLowerCase();
      return list.filter(
        (m) =>
          isMemberFlagTrue(m.youth_camp_attended) &&
          String(m.youth_camp_group || "").toLowerCase() === group,
      );
    },
    [filterYouthCamp],
  );

  const applySpiritualStatusFilter = useCallback(
    (list) => {
      const f = String(filterSpiritualStatus || "").trim();
      if (!f) return list;
      if (f === MEMBER_SPIRITUAL_STATUS_FILTER_NO_ENCOUNTER) {
        return list.filter((m) => !isMemberFlagTrue(m.encuentro_attended));
      }
      if (f === MEMBER_SPIRITUAL_STATUS_FILTER_NO_BAPTIZED) {
        return list.filter((m) => !isMemberFlagTrue(m.baptized));
      }
      if (f === MEMBER_SPIRITUAL_STATUS_FILTER_NO_MENTOR) {
        return list.filter((m) => !mentorFormStateFromMember(m).has_mentor);
      }
      return list;
    },
    [filterSpiritualStatus],
  );

  const applySearchFilter = useCallback(
    (list) => {
      const q = filterSearch.trim();
      if (!q) return list;
      return list.filter((m) => memberMatchesSearch(m, q));
    },
    [filterSearch],
  );

  const filteredMembers = useMemo(() => {
    // 1) Base: miembros activos (papelera excluida). Mismo origen con o sin célula.
    let result = (members || []).filter((m) => !m.deleted_at);

    // 2) Filtro de célula (opcional). «Todas las células» = sin filtrar por célula.
    if (filterCell) {
      if (filterMode === "parent_enlace") {
        result = getDirectChildLeaderMembers(filterCell, cells, result).map(({ member, roleLabel }) => ({
          ...member,
          childLeaderRoleLabel: roleLabel,
        }));
      } else {
        result = result
          .filter((m) => memberMatchesCellFilter(m, filterCell, selectedFilterCell))
          .map((m) => ({ ...m, childLeaderRoleLabel: null }));
      }
    } else {
      result = result
        .slice()
        .sort((a, b) => {
          const aUn = !String(a.cell_id || "").trim();
          const bUn = !String(b.cell_id || "").trim();
          if (aUn !== bUn) return aUn ? -1 : 1;
          return String(a.name || "").localeCompare(String(b.name || ""), "es");
        })
        .map((m) => ({ ...m, childLeaderRoleLabel: null }));
    }

    // 3) Sexo → Estado espiritual → Campamento → Búsqueda (independientes entre sí)
    result = applySexFilter(result);
    result = applySpiritualStatusFilter(result);
    result = applyYouthCampFilter(result);
    result = applySearchFilter(result);
    return result;
  }, [
    members,
    cells,
    filterCell,
    filterMode,
    selectedFilterCell,
    applySexFilter,
    applySpiritualStatusFilter,
    applyYouthCampFilter,
    applySearchFilter,
  ]);

  const emptyMembersMessage = useMemo(() => {
    if (filterSearch.trim()) return "No se encontraron miembros con esta búsqueda.";
    if (filterSpiritualStatus) {
      const statusLabel = spiritualStatusFilterLabel(filterSpiritualStatus);
      if (statusLabel) return `No hay miembros que cumplan con: ${statusLabel}.`;
    }
    if (filterYouthCamp) {
      if (filterYouthCamp === MEMBER_YOUTH_CAMP_FILTER_ALL_GROUPS) {
        return "No hay miembros que hayan asistido al Campamento de Jóvenes.";
      }
      const groupLabel = youthCampGroupLabel(filterYouthCamp);
      if (groupLabel) {
        return `No hay miembros del grupo ${groupLabel} que hayan asistido al Campamento de Jóvenes.`;
      }
    }
    if (!filterCell) return "No hay miembros registrados. Crea el primer miembro.";
    if (filterMode === "parent_enlace") {
      return "No hay líderes ni co-líderes en las células hijas directas de esta célula madre/enlace.";
    }
    return "No hay miembros registrados para esta célula.";
  }, [filterCell, filterMode, filterSearch, filterSpiritualStatus, filterYouthCamp]);

  const exportReportTitle = useMemo(
    () =>
      buildMembersFilterReportTitle({
        filterCell,
        filterMode,
        cells,
        filterYouthCamp,
        filterSpiritualStatus,
      }),
    [filterCell, filterMode, cells, filterYouthCamp, filterSpiritualStatus],
  );

  const runFilteredExport = (format) => {
    setExportBusy(true);
    try {
      const rows = buildMembersExportRows(filteredMembers, getCellName, formatMentor);
      if (format === "pdf") exportMembersFilteredPdf(rows, exportReportTitle);
      else if (format === "csv") exportMembersFilteredCsv(rows, exportReportTitle);
      else exportMembersFilteredExcel(rows, exportReportTitle);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Exportación miembros:", err);
      alert(err?.message || "No se pudo exportar");
    } finally {
      setExportBusy(false);
    }
  };

  const getMemberTypeLabel = (type) => {
    const types = {
      invitado_potencial: "Invitado Potencial",
      invitado_primer_mes: "Invitado 1er Mes",
      visita_regular: "Visita Regular",
      visita_esporadica: "Visita Esporádica",
      visita: "Visita",
      miembro: "Miembro",
      invitado: "Invitado",
    };
    return types[type] || type;
  };

  const getMemberTypeBadgeColor = (type) => {
    const colors = {
      invitado_potencial: "bg-pink-100 text-pink-800",
      invitado_primer_mes: "bg-indigo-100 text-indigo-800",
      visita_regular: "bg-purple-100 text-purple-800",
      visita_esporadica: "bg-yellow-100 text-yellow-800",
      visita: "bg-blue-100 text-blue-800",
      miembro: "bg-green-100 text-green-800",
      invitado: "bg-gray-100 text-gray-800",
      lider: "bg-slate-200 text-slate-900",
      líder: "bg-slate-200 text-slate-900",
      supervisor: "bg-slate-300 text-slate-900",
      supervisora: "bg-slate-300 text-slate-900",
      pastor: "bg-blue-200 text-blue-900",
      pastora: "bg-blue-200 text-blue-900",
    };
    return colors[type] || "bg-gray-100 text-gray-800";
  };

  const tabs = [
    { value: "basic", label: "Básico" },
    { value: "spiritual", label: "Espiritual" },
    { value: "service", label: "Servicio" },
    { value: "status", label: "Estado" },
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <h2 className="text-2xl font-bold text-gray-800">Miembros</h2>
        <div className="flex flex-wrap gap-2 w-full md:w-auto">
          <div className="w-full md:w-72">
            <Label htmlFor="member-search" className="text-sm text-gray-600 mb-1 block">
              Buscar miembro…
            </Label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" aria-hidden />
              <Input
                id="member-search"
                type="search"
                value={filterSearch}
                onChange={(e) => setFilterSearch(e.target.value)}
                placeholder="Escriba nombre o teléfono…"
                className="pl-9"
              />
            </div>
          </div>
          {canTrashMembers ? (
            <Button
              type="button"
              variant="outline"
              className="border-amber-300 text-amber-900 hover:bg-amber-50"
              onClick={() => setShowTrash(true)}
            >
              <Archive className="w-4 h-4 mr-2" />
              Papelera
            </Button>
          ) : null}
          <NativeSelect
            value={filterCell}
            onChange={(v) => {
              setFilterCell(v);
              if (!v) setFilterMode("direct");
            }}
            placeholder="Todas las células"
            className="w-full md:w-48"
          >
            <option value="">Todas las células</option>
            {cells.map((cell) => {
              const cid = getCellMongoId(cell);
              return (
              <option key={cid || cell.name} value={cid}>
                {cell.name}
              </option>
            );})}
          </NativeSelect>
          {filterCell ? (
            <NativeSelect
              value={filterMode}
              onChange={setFilterMode}
              className="w-full md:w-56"
            >
              <option value="direct">Miembros de la célula</option>
              <option value="parent_enlace">Célula madre / Enlace (hijas directas)</option>
            </NativeSelect>
          ) : null}
          <NativeSelect
            value={filterSex}
            onChange={setFilterSex}
            className="w-full md:w-44"
          >
            <option value={MEMBER_SEX_FILTER_ALL}>Todos (sexo)</option>
            <option value={MEMBER_SEX_M}>Masculino</option>
            <option value={MEMBER_SEX_F}>Femenino</option>
            <option value={MEMBER_SEX_FILTER_UNSPECIFIED}>Sin especificar</option>
          </NativeSelect>
          <NativeSelect
            value={filterYouthCamp}
            onChange={setFilterYouthCamp}
            placeholder="Campamento de Jóvenes"
            className="w-full md:w-48"
          >
            <option value={MEMBER_YOUTH_CAMP_FILTER_ALL_GROUPS}>Todos los grupos</option>
            {YOUTH_CAMP_GROUP_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </NativeSelect>
          <NativeSelect
            value={filterSpiritualStatus}
            onChange={setFilterSpiritualStatus}
            placeholder="Estado espiritual"
            className="w-full md:w-52"
          >
            {MEMBER_SPIRITUAL_STATUS_FILTER_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </NativeSelect>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            disabled={exportBusy}
            onClick={() => runFilteredExport("xlsx")}
            {...excelExportButtonA11y}
          >
            <FileSpreadsheet className="w-4 h-4 shrink-0" aria-hidden />
            <ExcelExportLabel />
          </Button>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            disabled={exportBusy}
            onClick={() => runFilteredExport("csv")}
          >
            <FileText className="w-4 h-4 mr-1" />
            Exportar CSV
          </Button>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            disabled={exportBusy}
            onClick={() => runFilteredExport("pdf")}
          >
            <FileText className="w-4 h-4 mr-1" />
            Exportar PDF
          </Button>
          {canManage && (
            <Button onClick={() => setShowForm(true)} className="bg-blue-600 hover:bg-blue-700">
              <Plus className="w-4 h-4 mr-2" /> Nuevo
            </Button>
          )}
        </div>
      </div>

      <MembersTrashDialog
        open={showTrash}
        onClose={() => setShowTrash(false)}
        canPermanentDelete={canPermanentDeleteMembers}
        onPermanentDeleted={onRefresh}
      />

      <MoveMemberToTrashDialog
        open={Boolean(trashTarget)}
        member={trashTarget}
        onClose={() => !trashSubmitting && setTrashTarget(null)}
        onConfirm={handleConfirmTrash}
        submitting={trashSubmitting}
      />

      <NativeDialog open={showForm} onClose={resetForm} className="w-full max-w-4xl p-6">
        <h2 className="text-xl font-bold mb-4">{editingMember ? "Editar Miembro" : "Nuevo Miembro"}</h2>
        <form onSubmit={handleSubmit}>
          <NativeTabs tabs={tabs} activeTab={activeTab} onTabChange={setActiveTab}>
            {activeTab === "basic" && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2 md:col-span-2">
                  <Label>Célula</Label>
                  <NativeSelect
                    value={formData.cell_id}
                    onChange={(v) => setFormData({ ...formData, cell_id: v })}
                    placeholder="Sin célula asignada"
                  >
                    <option value="">Sin célula asignada</option>
                    {cells.map((cell) => {
                      const cid = getCellMongoId(cell);
                      return (
                      <option key={cid || cell.name} value={cid}>
                        {cell.name}
                      </option>
                    );})}
                  </NativeSelect>
                  {editingMemberDoc ? (
                    <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700 space-y-1">
                      <p>
                        <span className="font-medium text-slate-800">Célula actual (ficha miembro):</span>{" "}
                        {getCellName(formData.cell_id)}{" "}
                        {formData.cell_id ? (
                          <span className="text-slate-500">(ID {formData.cell_id})</span>
                        ) : null}
                      </p>
                      {cargoInfoForEditing.length ? (
                        <div>
                          <span className="font-medium text-slate-800">Cargo actual:</span>
                          <ul className="list-disc pl-5 mt-1 space-y-0.5">
                            {cargoInfoForEditing.map((row) => (
                              <li key={`${row.label}-${row.cellId}`}>
                                {row.label} — {row.cellName}{" "}
                                <span className="text-slate-500">(célula ID {row.cellId})</span>
                              </li>
                            ))}
                          </ul>
                          <p className="mt-2 text-xs text-amber-800">
                            Para cambiar a otra célula, primero debe quitar estos cargos en el módulo Células; luego podrá mover al miembro aquí.
                          </p>
                        </div>
                      ) : (
                        <p className="text-xs text-slate-600">
                          Sin cargos pastorales registrados para este miembro en otras células.
                        </p>
                      )}
                    </div>
                  ) : null}
                </div>
                <div className="space-y-2">
                  <Label>Tipo de Miembro *</Label>
                  <NativeSelect
                    value={formData.member_type}
                    onChange={(v) => setFormData({ ...formData, member_type: v })}
                  >
                    <option value="invitado_potencial">Invitado Potencial</option>
                    <option value="invitado_primer_mes">Invitado 1er Mes</option>
                    <option value="visita_esporadica">Visita Esporádica</option>
                    <option value="visita_regular">Visita Regular</option>
                    <option value="miembro">Miembro</option>
                    <option value="pastor">Pastor</option>
                    <option value="pastora">Pastora</option>
                  </NativeSelect>
                </div>
                <div className="space-y-2">
                  <Label>Nombre Completo *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Teléfono *</Label>
                  <Input
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Sexo *</Label>
                  <NativeSelect
                    value={formData.sex}
                    onChange={(v) => setFormData({ ...formData, sex: v })}
                    required={!editingMember}
                  >
                    {editingMember ? (
                      <option value="">Sin especificar</option>
                    ) : (
                      <option value="">Seleccione…</option>
                    )}
                    <option value={MEMBER_SEX_M}>Masculino</option>
                    <option value={MEMBER_SEX_F}>Femenino</option>
                  </NativeSelect>
                </div>
                <div className="space-y-2">
                  <Label>Edad</Label>
                  <Input
                    type="number"
                    value={formData.age}
                    onChange={(e) => setFormData({ ...formData, age: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Fecha de Nacimiento</Label>
                  <Input
                    type="date"
                    value={formData.birth_date}
                    onChange={(e) => setFormData({ ...formData, birth_date: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Estado Civil</Label>
                  <NativeSelect
                    value={formData.marital_status}
                    onChange={(v) => setFormData({ ...formData, marital_status: v })}
                    placeholder="Seleccionar"
                  >
                    <option value="soltero">Soltero(a)</option>
                    <option value="casado">Casado(a)</option>
                    <option value="divorciado">Divorciado(a)</option>
                    <option value="viudo">Viudo(a)</option>
                    <option value="union_libre">Unión Libre</option>
                  </NativeSelect>
                </div>
                <div className="space-y-2">
                  <Label>Grado Académico</Label>
                  <NativeSelect
                    value={formData.academic_degree}
                    onChange={(v) => setFormData({ ...formData, academic_degree: v })}
                    placeholder="Seleccionar"
                  >
                    <option value="primaria">Primaria</option>
                    <option value="secundaria">Secundaria</option>
                    <option value="bachillerato">Bachillerato</option>
                    <option value="tecnico">Técnico</option>
                    <option value="universidad">Universidad</option>
                    <option value="posgrado">Posgrado</option>
                  </NativeSelect>
                </div>
                <div className="space-y-2 md:col-span-2">
                  <Label>Zona/Dirección</Label>
                  <Input
                    value={formData.address_zone}
                    onChange={(e) => setFormData({ ...formData, address_zone: e.target.value })}
                  />
                </div>
              </div>
            )}

            {activeTab === "spiritual" && (
              <MemberSpiritualTabFields key="member-spiritual-tab-v3" formData={formData} setFormData={setFormData} />
            )}

            {activeTab === "service" && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-center justify-between p-4 border rounded-lg">
                  <div>
                    <Label>¿Asiste a los Servicios?</Label>
                    <p className="text-sm text-gray-500">Cultos dominicales</p>
                  </div>
                  <NativeSwitch
                    checked={formData.attends_services}
                    onCheckedChange={(v) => setFormData({ ...formData, attends_services: v })}
                  />
                </div>
                {formData.attends_services && (
                  <div className="space-y-2">
                    <Label>Tipo de Servicio</Label>
                    <NativeSelect
                      value={formData.service_type}
                      onChange={(v) => setFormData({ ...formData, service_type: v })}
                      placeholder="Seleccionar"
                    >
                      <option value="primer">Primer Servicio</option>
                      <option value="segundo">Segundo Servicio</option>
                      <option value="ambos">Ambos Servicios</option>
                    </NativeSelect>
                  </div>
                )}
                {!formData.attends_services && (
                  <div className="space-y-2">
                    <Label>Razón de No Asistencia</Label>
                    <Input
                      value={formData.non_attendance_reason}
                      onChange={(e) => setFormData({ ...formData, non_attendance_reason: e.target.value })}
                      placeholder="¿Por qué no asiste?"
                    />
                  </div>
                )}
                <div className="flex items-center justify-between p-4 border rounded-lg">
                  <div>
                    <Label>¿Asiste a Célula Regularmente?</Label>
                    <p className="text-sm text-gray-500">Reunión semanal</p>
                  </div>
                  <NativeSwitch
                    checked={formData.attends_cell_regularly}
                    onCheckedChange={(v) => setFormData({ ...formData, attends_cell_regularly: v })}
                  />
                </div>
                <div className="flex items-center justify-between p-4 border rounded-lg">
                  <div>
                    <Label>¿Activo en Servicio?</Label>
                    <p className="text-sm text-gray-500">Sirve en la iglesia</p>
                  </div>
                  <NativeSwitch
                    checked={formData.active_in_service}
                    onCheckedChange={(v) => setFormData({ ...formData, active_in_service: v })}
                  />
                </div>
                <div className="flex items-center justify-between p-4 border rounded-lg">
                  <div>
                    <Label>¿Sirve en un Departamento?</Label>
                    <p className="text-sm text-gray-500">Ministerio específico</p>
                  </div>
                  <NativeSwitch
                    checked={formData.serves_in_department}
                    onCheckedChange={(v) => setFormData({ ...formData, serves_in_department: v })}
                  />
                </div>
                {formData.serves_in_department && (
                  <div className="space-y-2">
                    <Label>Departamento de Servicio</Label>
                    <Input
                      value={formData.service_department}
                      onChange={(e) => setFormData({ ...formData, service_department: e.target.value })}
                      placeholder="Ej: Alabanza, Ujieres, etc."
                    />
                  </div>
                )}
                <div className="space-y-2">
                  <Label>Departamento Deseado</Label>
                  <Input
                    value={formData.desired_department}
                    onChange={(e) => setFormData({ ...formData, desired_department: e.target.value })}
                    placeholder="¿En qué le gustaría servir?"
                  />
                </div>
              </div>
            )}

            {activeTab === "status" && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-center justify-between p-4 border rounded-lg border-red-200 bg-red-50">
                  <div>
                    <Label className="text-red-700">¿Inactivo +2 Meses?</Label>
                    <p className="text-sm text-red-500">No ha asistido en 2+ meses</p>
                  </div>
                  <NativeSwitch
                    checked={formData.inactive_2_months}
                    onCheckedChange={(v) => setFormData({ ...formData, inactive_2_months: v })}
                  />
                </div>
                <div className="flex items-center justify-between p-4 border rounded-lg border-orange-200 bg-orange-50">
                  <div>
                    <Label className="text-orange-700">¿Requiere Cuidado Especial?</Label>
                    <p className="text-sm text-orange-500">Atención prioritaria</p>
                  </div>
                  <NativeSwitch
                    checked={formData.extra_care}
                    onCheckedChange={(v) => setFormData({ ...formData, extra_care: v })}
                  />
                </div>
                <div className="flex items-center justify-between p-4 border rounded-lg">
                  <div>
                    <Label>¿Dejó de Asistir?</Label>
                    <p className="text-sm text-gray-500">Ya no viene a la iglesia</p>
                  </div>
                  <NativeSwitch
                    checked={formData.stopped_attending}
                    onCheckedChange={(v) => setFormData({ ...formData, stopped_attending: v })}
                  />
                </div>
                {formData.stopped_attending && (
                  <div className="space-y-2">
                    <Label>Razón</Label>
                    <Input
                      value={formData.stopped_reason}
                      onChange={(e) => setFormData({ ...formData, stopped_reason: e.target.value })}
                      placeholder="¿Por qué dejó de asistir?"
                    />
                  </div>
                )}
              </div>
            )}
          </NativeTabs>

          <div className="flex justify-end gap-2 pt-6 mt-4 border-t">
            <Button type="button" variant="outline" onClick={resetForm}>
              Cancelar
            </Button>
            <Button type="submit" className="bg-blue-600 hover:bg-blue-700">
              {editingMember ? "Actualizar" : "Crear"}
            </Button>
          </div>
        </form>
      </NativeDialog>

      <div className="grid gap-3">
        {filteredMembers.length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center text-gray-500">{emptyMembersMessage}</CardContent>
          </Card>
        ) : (
          filteredMembers.map((member) => (
            <Card
              key={member.id || member._id}
              className={`hover:shadow-md transition-shadow ${member.inactive_2_months ? "border-red-200 bg-red-50" : ""} ${member.extra_care ? "border-orange-200" : ""}`}
            >
              <CardContent className="p-4">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      <h3 className="text-lg font-semibold text-gray-800">{member.name}</h3>
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-medium ${getMemberTypeBadgeColor(member.member_type)}`}
                      >
                        {getMemberTypeLabel(member.member_type)}
                      </span>
                      {member.inactive_2_months && (
                        <Badge variant="destructive" className="text-xs">
                          Inactivo
                        </Badge>
                      )}
                      {member.extra_care && <Badge className="bg-orange-500 text-xs">Cuidado Especial</Badge>}
                      {member.childLeaderRoleLabel ? (
                        <Badge className="bg-teal-100 text-teal-900 text-xs">{member.childLeaderRoleLabel}</Badge>
                      ) : null}
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-2 text-sm text-gray-600">
                      <p>
                        <Phone className="w-4 h-4 inline mr-1" />
                        {member.phone || "Sin teléfono"}
                      </p>
                      <p>
                        <User className="w-4 h-4 inline mr-1" />
                        {memberSexLabel(member.sex)}
                      </p>
                      <p>
                        <Building2 className="w-4 h-4 inline mr-1" />
                        {getCellName(member.cell_id)}
                      </p>
                      <p>
                        {member.age ? `${member.age} años` : "Sin edad"}
                      </p>
                    </div>
                    <p className="mt-2 text-sm text-gray-600">
                      <span className="font-medium text-gray-700">Mentor:</span> {formatMentor(member)}
                    </p>
                    <p
                      className="mt-2 font-mono text-xs text-gray-500 break-all"
                      title="Para registro de usuario líder o supervisor"
                    >
                      <span className="font-sans font-medium text-gray-600">ID de registro (member_id):</span>{" "}
                      {member.id || member._id}
                    </p>
                  </div>
                  {(canManage || canTrashMembers) && (
                    <div className="flex gap-2">
                      {canManage ? (
                        <Button size="sm" variant="outline" onClick={() => handleEdit(member)}>
                          <Edit className="w-4 h-4" />
                        </Button>
                      ) : null}
                      {canTrashMembers ? (
                        <Button
                          size="sm"
                          variant="destructive"
                          title="Mover a papelera"
                          onClick={() => openMoveToTrash(member)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      ) : null}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
}
